import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function MultiAgentContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">多智能体路由</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 支持多个智能体并行运行，通过路由规则将不同用户或渠道的消息分发到对应的智能体。
        </p>
      </div>

      <SectionTitle id="multi-what">什么是"一个智能体"</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        在 OpenClaw 中，一个智能体对应一个独立的 AI 人格，拥有自己的：
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
        {[
          { icon: 'ri-brain-line', label: '系统提示' },
          { icon: 'ri-database-2-line', label: '记忆文件' },
          { icon: 'ri-tools-line', label: '工具集' },
          { icon: 'ri-robot-line', label: 'AI 模型' },
        ].map((item) => (
          <div key={item.label} className="flex flex-col items-center gap-2 bg-slate-800/40 border border-slate-700/50 rounded-lg p-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`${item.icon} text-cyan-400 text-lg`}></i>
            </div>
            <span className="text-xs text-slate-300 text-center">{item.label}</span>
          </div>
        ))}
      </div>

      <SectionTitle id="multi-paths">路径（快速映射）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个智能体通过路径（Path）与渠道绑定，路径决定了哪些消息会路由到该智能体：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "assistant-cn",
      name: "中文助手",
      // 路径：匹配来自 Telegram 的消息
      paths: ["telegram/*"],
    },
    {
      id: "assistant-en",
      name: "English Assistant",
      // 路径：匹配来自 Discord 的消息
      paths: ["discord/*"],
    }
  ]
}`} />

      <SectionTitle id="multi-assistant">智能体助手</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体助手（Agent Assistant）是一种特殊的智能体，可以协调其他智能体完成复杂任务：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "coordinator",
      name: "协调者",
      systemPrompt: "你是一个任务协调者，负责将用户请求分配给合适的专业智能体。",
      // 可以调用的子智能体
      subAgents: ["researcher", "writer", "coder"],
    },
    {
      id: "researcher",
      name: "研究员",
      systemPrompt: "你是一个专业的研究员，擅长信息检索和分析。",
    },
    {
      id: "writer",
      name: "写作助手",
      systemPrompt: "你是一个专业的写作助手，擅长文章创作和编辑。",
    }
  ]
}`} />

      <SectionTitle id="multi-personalities">多个智能体 = 多个人 / 多种人格</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        不同的智能体可以扮演完全不同的角色，就像不同的人一样：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
        {[
          { name: '工作助手', desc: '专业、严谨，处理工作相关事务', icon: 'ri-briefcase-line', color: 'cyan' },
          { name: '生活助手', desc: '轻松、友好，处理日常生活问题', icon: 'ri-home-smile-line', color: 'emerald' },
          { name: '学习助手', desc: '耐心、详细，帮助学习和理解', icon: 'ri-book-open-line', color: 'amber' },
        ].map((item) => (
          <div key={item.name} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
            <div className="w-8 h-8 flex items-center justify-center mb-2">
              <i className={`${item.icon} text-${item.color}-400 text-lg`}></i>
            </div>
            <div className="text-sm font-semibold text-white mb-1">{item.name}</div>
            <div className="text-xs text-slate-400">{item.desc}</div>
          </div>
        ))}
      </div>

      <SectionTitle id="multi-whatsapp-split">一个 WhatsApp 号码多个人（私信分割）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        同一个 WhatsApp 号码可以根据发送者路由到不同的智能体：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "vip-agent",
      name: "VIP 专属助手",
      paths: ["whatsapp/*"],
      routing: {
        // 只处理 VIP 用户列表中的消息
        allowFrom: ["+8613800138001", "+8613800138002"],
      }
    },
    {
      id: "general-agent",
      name: "通用助手",
      paths: ["whatsapp/*"],
      routing: {
        // 处理所有其他用户的消息
        denyFrom: ["+8613800138001", "+8613800138002"],
      }
    }
  ]
}`} />

      <SectionTitle id="multi-routing-rules">路由规则</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        路由规则支持多种匹配条件：
      </p>
      <CodeBlock language="json5" code={`{
  routing: {
    rules: [
      {
        // 条件：来自特定渠道
        condition: { channel: "telegram" },
        // 路由到：telegram 专属智能体
        agent: "telegram-agent",
      },
      {
        // 条件：来自特定用户
        condition: { peerId: "user_123456" },
        agent: "personal-agent",
      },
      {
        // 条件：消息包含特定关键词
        condition: { messageContains: ["紧急", "urgent"] },
        agent: "priority-agent",
      },
      {
        // 默认规则（无条件匹配）
        condition: {},
        agent: "default-agent",
      }
    ]
  }
}`} />

      <SectionTitle id="multi-accounts">多账户 / 电话号码</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        可以为同一渠道配置多个账户，每个账户绑定不同的智能体：
      </p>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "whatsapp",
      id: "wa-business",
      // 商务号码
      account: "+8613800138000",
    },
    {
      type: "whatsapp",
      id: "wa-personal",
      // 个人号码
      account: "+8613900139000",
    }
  ],
  agents: [
    {
      id: "business-agent",
      paths: ["wa-business/*"],
    },
    {
      id: "personal-agent",
      paths: ["wa-personal/*"],
    }
  ]
}`} />

      <SectionTitle id="multi-concepts">概念</SectionTitle>
      <ul className="space-y-2.5 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-route-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">路径（Path）：</strong>定义智能体监听哪些渠道和对等方的消息</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-filter-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">路由规则（Routing Rule）：</strong>基于条件将消息分发到特定智能体</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-box-3-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">沙箱（Sandbox）：</strong>每个智能体在独立的沙箱中运行，互不干扰</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-share-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">共享渠道：</strong>多个智能体可以共享同一个渠道，通过路由规则区分</span></li>
      </ul>

      <SectionTitle id="multi-example1">示例：两个 WhatsApp → 两个智能体</SectionTitle>
      <CodeBlock language="json5" code={`// 两个 WhatsApp 号码分别绑定两个智能体
{
  channels: [
    { type: "whatsapp", id: "wa1", account: "+8613800000001" },
    { type: "whatsapp", id: "wa2", account: "+8613800000002" },
  ],
  agents: [
    {
      id: "agent-alice",
      name: "Alice",
      systemPrompt: "你是 Alice，一个友好的客服助手。",
      paths: ["wa1/*"],
    },
    {
      id: "agent-bob",
      name: "Bob",
      systemPrompt: "你是 Bob，一个专业的技术支持工程师。",
      paths: ["wa2/*"],
    }
  ]
}`} />

      <SectionTitle id="multi-example2">示例：WhatsApp 日常聊天 + Telegram 深度工作</SectionTitle>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "casual-agent",
      name: "日常助手",
      model: "claude-3-5-haiku-20241022",  // 快速模型
      systemPrompt: "你是一个轻松友好的日常聊天助手。",
      paths: ["whatsapp/*"],
    },
    {
      id: "work-agent",
      name: "工作助手",
      model: "claude-3-5-sonnet-20241022",  // 强力模型
      systemPrompt: "你是一个专业的工作助手，擅长深度分析和复杂任务。",
      paths: ["telegram/*"],
    }
  ]
}`} />

      <SectionTitle id="multi-example3">示例：同一渠道一个对等方到 Opus</SectionTitle>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "opus-agent",
      name: "高级助手",
      model: "claude-3-opus-20240229",
      paths: ["telegram/*"],
      routing: {
        // 只有特定用户使用 Opus 模型
        allowFrom: ["user_vip_123"],
      }
    },
    {
      id: "standard-agent",
      name: "标准助手",
      model: "claude-3-5-haiku-20241022",
      paths: ["telegram/*"],
      routing: {
        // 其他所有用户使用标准模型
        denyFrom: ["user_vip_123"],
      }
    }
  ]
}`} />

      <SectionTitle id="multi-family">绑定到 WhatsApp 群组的家庭智能体</SectionTitle>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "family-agent",
      name: "家庭助手",
      systemPrompt: "你是家庭的 AI 助手，了解每个家庭成员的偏好和需求。",
      paths: ["whatsapp/group_family_*"],
      memory: {
        // 家庭共享记忆
        filePath: "family-memory.md",
      }
    }
  ]
}`} />

      <SectionTitle id="multi-sandbox">每智能体沙箱和工具配置</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个智能体在独立的沙箱中运行，可以配置不同的工具集和权限：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      id: "restricted-agent",
      name: "受限助手",
      sandbox: {
        // 禁用文件系统访问
        allowFileSystem: false,
        // 禁用网络访问
        allowNetwork: false,
        // 禁用 shell 命令执行
        allowShell: false,
      },
      // 只允许使用特定工具
      tools: {
        allowed: ["search_memory", "get_weather"],
        disabled: ["bash", "write_file", "read_file"],
      }
    },
    {
      id: "full-agent",
      name: "完整权限助手",
      sandbox: {
        allowFileSystem: true,
        allowNetwork: true,
        allowShell: true,
        // 限制可访问的目录
        allowedDirs: ["/home/user/projects"],
      }
    }
  ]
}`} />
      <InfoBox type="warning">
        为智能体配置适当的沙箱权限非常重要，特别是当智能体面向不受信任的用户时。最小权限原则可以有效降低安全风险。
      </InfoBox>
    </div>
  );
}
